import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { v4 as uuid } from 'uuid';

import { AlertProps } from './alert-props.interface';

interface AlertItem extends AlertProps {
  id: string;
  timer: ReturnType<typeof setTimeout>;
}

const closeTimeout = 5000;

const htmlEntities: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * The toast renders its text as HTML so a success message can bold a name. Error, warning and info
 * toasts are plain text, often a server message quoting something a person typed, so they go in escaped.
 */
const asPlainText = (text: string): string => text.replace(/[&<>"']/g, (character) => htmlEntities[character]);

export const useAlertsStore = defineStore({
  id: 'alerts',
  state: (): {
    alerts: AlertItem[];
    /** Until then error toasts are dropped: something else is already explaining the failure */
    errorsHeldUntil: number;
  } => ({
    alerts: [],
    errorsHeldUntil: 0,
  }),
  getters: {},
  actions: {
    success(text: string): void {
      this.show({ text, variant: 'success' });
    },

    info(text: string): void {
      this.show({ text: asPlainText(text), variant: 'info' });
    },

    error(text: string): void {
      if (Date.now() < this.errorsHeldUntil) return;

      this.show({ text: asPlainText(text), variant: 'error' });
    },

    /**
     * Drops error toasts for a moment, for a failure a dialog already explains: the caller that
     * made the request still reports it its own way, often with a generic "try again".
     */
    holdErrors(milliseconds: number): void {
      this.errorsHeldUntil = Date.now() + milliseconds;
    },

    somethingWentWrong(): void {
      this.error('Something went wrong. Please try again later.');
    },

    warning(text: string): void {
      this.show({ text: asPlainText(text), variant: 'warning' });
    },

    catchError(error: any): void {
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message?.message || error.response?.data?.message;

        if (message) {
          return this.error(message);
        }
      }

      this.somethingWentWrong();
    },

    show(options: AlertProps): string {
      const id = `alert-${uuid()}`;

      this.alerts.push({
        id,
        ...options,
        timer: setTimeout(() => this.close(id), closeTimeout),
      });

      return id;
    },

    close(alertId: string) {
      const alertIndex = this.alerts.findIndex((alert) => alert.id === alertId);

      if (alertIndex === -1) {
        return;
      }

      const alert = this.alerts[alertIndex];

      clearTimeout(alert.timer);
      this.alerts.splice(alertIndex, 1);
    },
  },
});
